import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Search, Download, UserPlus, ChevronLeft, ChevronRight, CheckCircle2, Trash2, Mail, Pencil, Ban, RotateCcw, Users, AlertTriangle } from 'lucide-react';
import { PageHeader, Modal, Field, Empty, Spinner, StatusBadge, AuthImage, ConfirmDialog, CategoryBadge, fmtDate, fmtDateTime, dateInput } from '../../components/ui';
import api, { errMsg, downloadFile, blobErrMsg } from '../../services/api';

const FILTER_KEYS = ['search', 'status', 'className', 'country', 'city', 'profession', 'rcuVerified', 'deletionRequested', 'birthMonth', 'sort'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function Row({ label, children }) {
  return <div className="grid grid-cols-[140px_1fr] gap-2 py-1.5 text-sm"><dt className="text-gray-500">{label}</dt><dd className="min-w-0 break-words font-medium text-gray-800">{children || '—'}</dd></div>;
}

function EditMember({ member, options, onSaved, onClose }) {
  const [form, setForm] = useState({
    fullName: member.fullName || '', nameWithInitials: member.nameWithInitials || '', className: member.className || '',
    nicNumber: member.nicNumber || '', dateOfBirth: dateInput(member.dateOfBirth), email: member.email, phone: member.phone,
    rcuCardNumber: member.rcuCardNumber || '', rcuRegisteredDate: dateInput(member.rcuRegisteredDate),
    city: member.city || '', country: member.country || '', profession: member.profession || '',
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.nicNumber) delete payload.nicNumber;
      const { data } = await api.put(`/admin/members/${member.id}`, payload);
      if (data.email !== member.email) toast('Email changed — a verification email was sent to the member', { icon: '✉️' });
      toast.success('Member updated');
      onSaved(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Edit member" wide onClose={onClose} footer={<>
      <button className="btn-outline" onClick={onClose}>Cancel</button>
      <button className="btn-primary" onClick={save} disabled={busy}>{busy && <Spinner size={16} />}Save</button>
    </>}>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Full name" className="sm:col-span-2"><input className="input" value={form.fullName} onChange={e => set('fullName', e.target.value)} /></Field>
        <Field label="Name with initials"><input className="input" value={form.nameWithInitials} onChange={e => set('nameWithInitials', e.target.value)} /></Field>
        <Field label="Class">
          <input className="input" list="class-options" value={form.className} onChange={e => set('className', e.target.value)} />
          <datalist id="class-options">{options.classNames.map(c => <option key={c} value={c} />)}</datalist>
        </Field>
        <Field label="NIC number" hint="Changing it updates the date of birth"><input className="input uppercase" value={form.nicNumber} onChange={e => set('nicNumber', e.target.value)} /></Field>
        <Field label="Date of birth" hint={member.hasSriLankanNic ? 'Read from NIC' : undefined}>
          <input type="date" className="input" disabled={member.hasSriLankanNic} value={form.dateOfBirth} onChange={e => set('dateOfBirth', e.target.value)} />
        </Field>
        <Field label="Email" hint="Changing it requires the member to verify again"><input type="email" className="input" value={form.email} onChange={e => set('email', e.target.value)} /></Field>
        <Field label="Mobile"><input className="input" value={form.phone} onChange={e => set('phone', e.target.value)} /></Field>
        <Field label="RCU card number"><input className="input" value={form.rcuCardNumber} onChange={e => set('rcuCardNumber', e.target.value)} /></Field>
        <Field label="RCU registered date"><input type="date" className="input" value={form.rcuRegisteredDate} onChange={e => set('rcuRegisteredDate', e.target.value)} /></Field>
        <Field label="City"><input className="input" value={form.city} onChange={e => set('city', e.target.value)} /></Field>
        <Field label="Country"><input className="input" value={form.country} onChange={e => set('country', e.target.value)} /></Field>
        <Field label="Profession" className="sm:col-span-2"><input className="input" value={form.profession} onChange={e => set('profession', e.target.value)} /></Field>
      </div>
    </Modal>
  );
}

function MemberDetail({ id, options, onClose, onChanged }) {
  const [m, setM] = useState(null);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(() => api.get(`/admin/members/${id}`).then(r => setM(r.data)), [id]);
  useEffect(() => { load(); }, [load]);

  const patch = async (url, body, msg) => {
    try {
      await api.patch(`/admin/members/${id}/${url}`, body);
      toast.success(msg);
      await load();
      onChanged();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const resend = async () => {
    try {
      await api.post(`/admin/members/${id}/resend-verification`);
      toast.success('Verification email sent');
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  if (!m) return <Modal title="Member" onClose={onClose} wide><div className="py-10 text-center"><Spinner /></div></Modal>;

  return (
    <>
      <Modal title={m.fullName} wide onClose={onClose}>
        {m.deletionRequestedAt && (
          <div className="mb-4 flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-800">
            <AlertTriangle size={16} /> This member asked for their data to be deleted on {fmtDate(m.deletionRequestedAt)}.
          </div>
        )}
        <div className="mb-4 flex flex-wrap gap-2">
          <StatusBadge status={m.status} />
          {m.rcuVerified ? <span className="badge bg-green-100 text-green-800"><CheckCircle2 size={12} />RCU confirmed</span> : <span className="badge bg-gold-100 text-gold-800">RCU not checked</span>}
          {m.unsubscribed && <span className="badge bg-gray-200 text-gray-700">Unsubscribed</span>}
          {m.pendingEmail && <span className="badge bg-amber-100 text-amber-800">Email change pending: {m.pendingEmail}</span>}
        </div>
        <div className="grid gap-6 md:grid-cols-[1fr_240px]">
          <dl className="divide-y">
            <Row label="Name with initials">{m.nameWithInitials}</Row>
            <Row label="Class">{m.className}</Row>
            <Row label="NIC">{m.nicNumber || (m.hasSriLankanNic ? '' : 'No Sri Lankan NIC')}</Row>
            <Row label="Date of birth">{fmtDate(m.dateOfBirth)}{m.gender && <span className="text-gray-400"> · {m.gender.toLowerCase()}</span>}</Row>
            <Row label="Email">{m.email} {m.emailVerifiedAt && <CheckCircle2 size={14} className="inline text-green-600" />}</Row>
            <Row label="Mobile">{m.phone && <a href={`tel:${m.phone}`} className="text-navy-700 hover:underline">{m.phone}</a>}</Row>
            <Row label="City / country">{[m.city, m.country].filter(Boolean).join(', ')}</Row>
            <Row label="Profession">{m.profession}</Row>
            <Row label="RCU card no.">{m.rcuCardNumber}</Row>
            <Row label="RCU registered">{fmtDate(m.rcuRegisteredDate)}</Row>
            <Row label="Registered">{fmtDateTime(m.createdAt)}</Row>
            <Row label="Email verified">{fmtDateTime(m.emailVerifiedAt)}</Row>
            <Row label="Details confirmed">{fmtDate(m.detailsConfirmedAt)}</Row>
            <Row label="Last login">{fmtDateTime(m.lastLoginAt)}</Row>
          </dl>
          <div>
            <p className="label">RCU membership card</p>
            {m.hasRcuCard ? <AuthImage src={`/admin/members/${m.id}/rcu-card`} alt="RCU card" className="w-full rounded-lg border object-contain" /> : <p className="text-sm text-gray-400">Not uploaded</p>}
            <button onClick={() => patch('rcu-verified', { rcuVerified: !m.rcuVerified }, m.rcuVerified ? 'Marked as not checked' : 'RCU membership confirmed')}
              className={`${m.rcuVerified ? 'btn-outline' : 'btn-gold'} btn-sm mt-3 w-full`}>
              <CheckCircle2 size={14} />{m.rcuVerified ? 'Undo RCU confirmation' : 'Confirm RCU membership'}
            </button>
          </div>
        </div>

        {m.deliveries?.length > 0 && (
          <div className="mt-6">
            <p className="label">Recent emails</p>
            <ul className="divide-y rounded-lg border text-sm">
              {m.deliveries.map(d => (
                <li key={d.id} className="flex items-center justify-between gap-2 px-3 py-2">
                  <span className="flex min-w-0 items-center gap-2"><CategoryBadge category={d.notification.category} /><span className="truncate">{d.notification.subject}</span></span>
                  <span className="flex-shrink-0 text-xs text-gray-500">{d.openedAt ? 'Opened' : d.status.toLowerCase()} · {fmtDate(d.sentAt)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-2 border-t pt-4">
          <button onClick={() => setEditing(true)} className="btn-primary btn-sm"><Pencil size={14} />Edit</button>
          {(!m.emailVerifiedAt || m.pendingEmail) && <button onClick={resend} className="btn-outline btn-sm"><Mail size={14} />Resend verification</button>}
          {m.status === 'DISABLED'
            ? <button onClick={() => patch('status', { status: m.emailVerifiedAt ? 'ACTIVE' : 'PENDING' }, 'Member re-enabled')} className="btn-outline btn-sm"><RotateCcw size={14} />Re-enable</button>
            : <button onClick={() => patch('status', { status: 'DISABLED' }, 'Member disabled')} className="btn-outline btn-sm"><Ban size={14} />Disable</button>}
          <button onClick={() => setConfirmDelete(true)} className="btn-danger btn-sm ml-auto"><Trash2 size={14} />Delete permanently</button>
        </div>
      </Modal>
      {editing && <EditMember member={m} options={options} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); onChanged(); }} />}
      {confirmDelete && (
        <ConfirmDialog title="Delete member?" danger confirmLabel="Delete"
          message={`This permanently deletes ${m.fullName}'s record, RCU card image and email history. This cannot be undone.`}
          onClose={() => setConfirmDelete(false)}
          onConfirm={async () => {
            await api.delete(`/admin/members/${m.id}`);
            toast.success('Member deleted');
            onChanged();
            onClose();
          }} />
      )}
    </>
  );
}

function InviteModal({ onClose }) {
  const [list, setList] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/admin/members/invite', { list });
      setResult(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title="Invite people to register" onClose={onClose} footer={result ? <button className="btn-primary" onClick={onClose}>Done</button> : <>
      <button className="btn-outline" onClick={onClose}>Cancel</button>
      <button className="btn-primary" onClick={send} disabled={busy || !list.trim()}>{busy && <Spinner size={16} />}Send invitations</button>
    </>}>
      {result ? (
        <div className="space-y-2 text-sm">
          <p className="text-green-700"><strong>{result.invited}</strong> invitation(s) sent.</p>
          {result.alreadyRegistered > 0 && <p className="text-gray-600">{result.alreadyRegistered} already registered — skipped.</p>}
          {result.invalid.length > 0 && <div className="text-red-700"><p>Could not invite:</p><ul className="list-disc pl-5">{result.invalid.map(l => <li key={l}>{l}</li>)}</ul></div>}
        </div>
      ) : (
        <Field label="Email addresses" hint="One per line. Optionally add a name after a comma, e.g. kasun@gmail.com, Kasun Perera">
          <textarea className="input font-mono" rows={10} value={list} onChange={e => setList(e.target.value)} placeholder={'kasun@gmail.com, Kasun Perera\nnuwan@yahoo.com'} />
        </Field>
      )}
    </Modal>
  );
}

export default function Members() {
  const [params, setParams] = useSearchParams();
  const filters = Object.fromEntries(FILTER_KEYS.map(k => [k, params.get(k) || '']));
  const page = Number(params.get('page')) || 1;
  const [data, setData] = useState(null);
  const [options, setOptions] = useState({ classNames: [], countries: [], cities: [], professions: [] });
  const [search, setSearch] = useState(filters.search);
  const [openId, setOpenId] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const query = params.toString();
  const load = useCallback(() => {
    api.get(`/admin/members?${query}`).then(r => setData(r.data));
  }, [query]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/admin/members/options').then(r => setOptions(r.data)); }, []);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    next.delete('page');
    setParams(next);
  };

  // Debounced search box
  useEffect(() => {
    const t = setTimeout(() => { if (search !== filters.search) setFilter('search', search); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const goPage = p => { const next = new URLSearchParams(params); next.set('page', p); setParams(next); };

  const doExport = async () => {
    setExporting(true);
    try {
      const q = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      await downloadFile('/admin/members/export', `members-${new Date().toISOString().slice(0, 10)}.xlsx`, q);
    } catch (err) {
      toast.error(await blobErrMsg(err, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const activeFilters = FILTER_KEYS.filter(k => k !== 'search' && k !== 'sort' && filters[k]).length;
  const Select = ({ k, label, children }) => (
    <select className="input w-auto min-w-[8rem] py-1.5" value={filters[k]} onChange={e => setFilter(k, e.target.value)} aria-label={label}>
      <option value="">{label}</option>{children}
    </select>
  );

  return (
    <div>
      <PageHeader title="Members" subtitle={data ? `${data.total} member${data.total === 1 ? '' : 's'} match` : ' '}
        actions={<>
          <button onClick={() => setInviting(true)} className="btn-outline"><UserPlus size={16} />Invite</button>
          <button onClick={doExport} disabled={exporting} className="btn-primary">{exporting ? <Spinner size={16} /> : <Download size={16} />}Export to Excel</button>
        </>} />

      <div className="card mb-4 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full sm:w-72">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input className="input pl-9" placeholder="Name, email, phone, NIC, card no." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <Select k="status" label="Any status"><option value="ACTIVE">Active</option><option value="PENDING">Unverified</option><option value="DISABLED">Disabled</option></Select>
          <Select k="className" label="Any class">{options.classNames.map(c => <option key={c}>{c}</option>)}</Select>
          <Select k="country" label="Any country">{options.countries.map(c => <option key={c}>{c}</option>)}</Select>
          <Select k="city" label="Any city">{options.cities.map(c => <option key={c}>{c}</option>)}</Select>
          <Select k="profession" label="Any profession">{options.professions.map(c => <option key={c}>{c}</option>)}</Select>
          <Select k="rcuVerified" label="RCU: any"><option value="true">RCU confirmed</option><option value="false">RCU not checked</option></Select>
          <Select k="birthMonth" label="Birth month">{MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}</Select>
          <Select k="deletionRequested" label="Deletion requests"><option value="true">Asked to be deleted</option></Select>
          <Select k="sort" label="Newest first"><option value="oldest">Oldest first</option><option value="name">Name A–Z</option><option value="class">Class</option></Select>
          {activeFilters > 0 && <button onClick={() => { setSearch(''); setParams({}); }} className="btn-ghost btn-sm">Clear filters</button>}
        </div>
      </div>

      <div className="card overflow-hidden">
        {!data ? <div className="py-16 text-center"><Spinner className="text-gray-300" /></div> : !data.rows.length ? (
          <Empty icon={Users} title="No members found">{activeFilters || filters.search ? 'Try clearing the filters.' : 'Share the registration link in your WhatsApp groups to get started.'}</Empty>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50"><tr>
                  <th className="th">Name</th><th className="th">Class</th><th className="th">Contact</th><th className="th">Location</th><th className="th">RCU</th><th className="th">Status</th><th className="th">Joined</th>
                </tr></thead>
                <tbody className="divide-y">
                  {data.rows.map(m => (
                    <tr key={m.id} onClick={() => setOpenId(m.id)} className="cursor-pointer hover:bg-navy-50/50">
                      <td className="td">
                        <p className="font-medium text-navy-900">{m.fullName}</p>
                        <p className="text-xs text-gray-400">{m.nicNumber || 'No NIC'}</p>
                      </td>
                      <td className="td">{m.className || '—'}</td>
                      <td className="td"><p>{m.email}</p><p className="text-xs text-gray-500">{m.phone}</p></td>
                      <td className="td text-gray-600">{[m.city, m.country].filter(Boolean).join(', ') || '—'}</td>
                      <td className="td">{m.rcuVerified ? <CheckCircle2 size={16} className="text-green-600" /> : <span className="text-xs text-gold-700">Check</span>}</td>
                      <td className="td">
                        <div className="flex flex-wrap gap-1"><StatusBadge status={m.status} />{m.deletionRequestedAt && <span className="badge bg-red-100 text-red-700">Delete?</span>}{m.unsubscribed && <span className="badge bg-gray-100 text-gray-600">Unsub.</span>}</div>
                      </td>
                      <td className="td whitespace-nowrap text-gray-500">{fmtDate(m.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between border-t px-4 py-3 text-sm text-gray-600">
              <span>Page {data.page} of {Math.max(1, Math.ceil(data.total / data.pageSize))}</span>
              <div className="flex gap-2">
                <button disabled={page <= 1} onClick={() => goPage(page - 1)} className="btn-outline btn-sm"><ChevronLeft size={14} />Prev</button>
                <button disabled={page * data.pageSize >= data.total} onClick={() => goPage(page + 1)} className="btn-outline btn-sm">Next<ChevronRight size={14} /></button>
              </div>
            </div>
          </>
        )}
      </div>

      {openId && <MemberDetail id={openId} options={options} onClose={() => setOpenId(null)} onChanged={load} />}
      {inviting && <InviteModal onClose={() => setInviting(false)} />}
    </div>
  );
}
